interface BrandingConfig {
  primary_color: string;
  secondary_color: string;
}

/**
 * Validates if a string is a valid hex color
 * Accepts formats: #RGB, #RRGGBB, RGB, RRGGBB
 */
function isValidHex(hex: string): boolean {
  if (!hex || typeof hex !== 'string') return false;
  
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');
  
  // Check if it's 3 or 6 characters and all are valid hex digits
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex);
}

export function updateThemeVariables(branding: Partial<BrandingConfig>) {
  const root = document.documentElement;
  const isDarkMode = root.classList.contains('dark');

  // CRITICAL: Clear all previous inline style overrides first
  // This ensures CSS variables from the stylesheet take effect properly
  const variablesToClear = [
    '--primary', '--primary-foreground', '--ring',
    '--sidebar-primary', '--sidebar-ring'
  ];
  
  variablesToClear.forEach(variable => {
    root.style.removeProperty(variable);
  });

  if (branding.primary_color) {
    // Validate hex color before processing
    if (!isValidHex(branding.primary_color)) {
      console.warn(`Invalid primary_color hex: ${branding.primary_color}. Skipping brand color application.`);
      return;
    }
    
    const hsl = hexToHSL(branding.primary_color);
    
    if (isDarkMode) {
      // Boost lightness for dark mode (ensure visibility and vibrancy)
      const adjustedPrimary = adjustLightnessForDarkMode(hsl, 60, 70); // 60-70% range
      root.style.setProperty('--primary', adjustedPrimary);
      
      // Primary foreground - dark color for contrast on buttons
      const darkForeground = adjustLightnessForDarkMode(hsl, 10, 15);
      root.style.setProperty('--primary-foreground', darkForeground);
      
      // Sidebar primary (same as primary in dark mode)
      root.style.setProperty('--sidebar-primary', adjustedPrimary);
      root.style.setProperty('--sidebar-ring', adjustedPrimary);
      root.style.setProperty('--ring', adjustedPrimary);
    } else {
      // Light mode - use brand color as-is or slightly adjust
      const adjustedPrimary = adjustLightnessForLightMode(hsl, 45, 55); // 45-55% range
      root.style.setProperty('--primary', adjustedPrimary);
      
      // Primary foreground - WHITE for contrast on buttons (critical!)
      root.style.setProperty('--primary-foreground', '210 40% 98%');
      
      // Sidebar in light mode uses dark color
      root.style.setProperty('--sidebar-primary', '240 5.9% 10%');
      root.style.setProperty('--sidebar-ring', adjustedPrimary);
      root.style.setProperty('--ring', adjustedPrimary);
    }
  } else {
    // Even without branding color, ensure primary-foreground is set correctly
    if (isDarkMode) {
      // Dark mode: dark text on lighter button background
      root.style.setProperty('--primary-foreground', '222.2 47.4% 11.2%');
    } else {
      // Light mode: WHITE text on colored button background
      root.style.setProperty('--primary-foreground', '210 40% 98%');
    }
  }

  // NOTE: We intentionally DO NOT apply secondary_color from branding
  // Secondary, accent, and muted colors are kept as professional neutral grays
  // This ensures:
  // - Consistent UX across all white-label clients
  // - Predictable component behavior in light/dark modes
  // - Better accessibility and contrast ratios
  // - Professional, clean appearance
  // 
  // Only the primary brand color is applied to maintain brand identity
  // while keeping the UI functional and professional
}

function adjustLightnessForDarkMode(hsl: string, minLight: number, maxLight: number): string {
  const parts = hsl.split(' ');
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]);
  const l = parseFloat(parts[2]);
  
  // Ensure lightness is within range for dark mode visibility
  let newLightness = l;
  if (l < minLight) newLightness = minLight;
  if (l > maxLight) newLightness = maxLight;
  
  // Adjust saturation based on original value
  let newSaturation = s;
  
  if (s > 80) {
    // Very saturated colors (neon-like) - reduce slightly for eye comfort in dark mode
    newSaturation = Math.min(s * 0.9, 75);
  } else if (s < 30) {
    // Low saturation (gray-ish) - boost slightly for better distinction
    newSaturation = Math.min(s * 1.2, 40);
  } else {
    // Normal saturation - boost slightly for vibrancy
    newSaturation = Math.min(s * 1.1, 85);
  }
  
  return `${h} ${Math.round(newSaturation)}% ${newLightness}%`;
}

function adjustLightnessForLightMode(hsl: string, minLight: number, maxLight: number): string {
  const parts = hsl.split(' ');
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]);
  const l = parseFloat(parts[2]);
  
  let newLightness = l;
  if (l < minLight) newLightness = minLight;
  if (l > maxLight) newLightness = maxLight;
  
  // Keep saturation as-is for primary color in light mode
  // No need to reduce saturation since this is only used for primary brand color
  const newSaturation = s;
  
  return `${h} ${Math.round(newSaturation)}% ${newLightness}%`;
}

function hexToHSL(hex: string): string {
  // Remove the # if present
  hex = hex.replace(/^#/, '');

  // Parse the hex values
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  // Convert to degrees and percentages
  const hDegrees = Math.round(h * 360);
  const sPercentage = Math.round(s * 100);
  const lPercentage = Math.round(l * 100);

  // Return in the format expected by CSS variables
  return `${hDegrees} ${sPercentage}% ${lPercentage}%`;
} 